<template>
  <div>
    <div
      class="import-background position-relative rounded-panel px-4 py-3 mt-4"
    >
      <div
        class="d-flex justify-content-between align-items-center py-2 cursor-pointer"
      >
        <div class="d-flex align-items-center flex-grow-1 min-w-0 me-2">
          <button
            type="button"
            class="import-panel-open d-flex align-items-center justify-content-between flex-grow-1 min-w-0 text-start border-0 bg-transparent text-white p-0"
            @click="showModal"
            aria-label="Import users from CSV, opens upload dialog"
          >
            <b
              class="text-value-lg fw-light d-block rounded-panel-title text-white"
            >
              Import Users
            </b>
            <span
              class="d-flex align-items-center justify-content-center text-white rounded-circle p-2 flex-shrink-0"
              aria-hidden="true"
            >
              <i class="flaticon-upload text-value-lg" aria-hidden="true"></i>
            </span>
          </button>
          <HelpCenterButton
            width="16"
            height="16"
            classes="ms-2 flex-shrink-0"
            content-key="import_users_user_menu"
            :is-old="true"
            aria-label="Import users help"
          />
        </div>
      </div>
    </div>

    <!-- Import Modal -->
    <CModal
      :scrollable="true"
      backdrop="static"
      :visible="state.isModalVisible"
      :close-on-backdrop="false"
      class="ps__child--consume"
      size="xl"
    >
      <div ref="importModalSurfaceRef" class="users-import-modal-surface">
        <div
          class="pt-4 px-4 create-popup-header d-flex align-items-center justify-content-between"
        >
          <button
            type="button"
            class="users-modal-close-btn d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal border-0"
            aria-label="Close"
            @click="closeImportModal"
          >
            <span class="close-icon" aria-hidden="true"></span>
          </button>
        </div>
        <Loader :is-processing="state.isLoading" />
        <CContainer class="px-5 py-4">
          <CRow>
            <CCol md="12">
              <h2>Steps for adding users by CSV upload</h2>
              <p class="fw-bold">
                This import option allows schools to provision users by loading
                a Comma-Separated Values (CSV) file. The file must contain a
                first name, last name, email, role, and status for all users. It
                is an option to also provide a Graduation Year and an ID number.
                The Graduation year should be in 4-digit format, 2022 not just
                22. You can leave this column blank if you do not wish to
                provide a graduation year, but you should not have either "null"
                or "0" zero in this column. It should either be four-digit years
                or blank. Likewise, if you do not wish to provide an ID number,
                just leave that column blank.
              </p>
              <p>
                1. Click Browse and locate the prepared user CSV file.<br />
                2. Click Submit.<br />
              </p>
              <p class="fw-bold">
                Here is a sample CSV user file in the required format:
                <a href="/csv-samples/users-import-sample.csv" download
                  >CSV File Format</a
                >. Do not modify headers or the order of columns.
              </p>
              <p class="fw-bold">
                Once you upload the file, if the file was successful, users will
                be imported and visible under Main Users. You can see the file
                progress on the "File Uploads" page.
              </p>
              <div>
                <div class="form-group">
                  <label
                    id="import-users-csv-label"
                    class="v3-form-label d-block mb-1"
                    for="import-users-csv-display"
                  >
                    Selected CSV file
                  </label>
                  <input
                    id="import-users-csv-display"
                    :class="{
                      'is-invalid':
                        Object.keys(state.reqResponse.errors).length > 0
                    }"
                    readonly
                    type="text"
                    class="form-control w-auto pe-5 cursor-pointer"
                    :value="state.params.file ? state.params.file.name : ''"
                    aria-labelledby="import-users-csv-label"
                    autocomplete="off"
                    @click="openFileIpnut('csvUsers')"
                    @keydown.enter.prevent="openFileIpnut('csvUsers')"
                    @keydown.space.prevent="openFileIpnut('csvUsers')"
                  />
                  <div
                    v-if="state.reqResponse.errors['email']"
                    class="invalid-feedback"
                  >
                    {{ state.reqResponse.errors["email"][0] }}
                  </div>
                  <div
                    v-if="state.reqResponse.errors['firstname']"
                    class="invalid-feedback"
                  >
                    {{ state.reqResponse.errors["firstname"][0] }}
                  </div>
                  <div
                    v-if="state.reqResponse.errors['lastname']"
                    class="invalid-feedback"
                  >
                    {{ state.reqResponse.errors["lastname"][0] }}
                  </div>
                  <div
                    v-if="state.reqResponse.errors['role']"
                    class="invalid-feedback"
                  >
                    {{ state.reqResponse.errors["role"][0] }}
                  </div>
                  <div
                    v-if="state.reqResponse.errors['status']"
                    class="invalid-feedback"
                  >
                    {{ state.reqResponse.errors["status"][0] }}
                  </div>
                  <div
                    v-if="state.reqResponse.errors['csv_file']"
                    class="invalid-feedback"
                  >
                    {{ state.reqResponse.errors["csv_file"][0] }}
                  </div>
                </div>
                <CAlert
                  v-if="state.reqResponse.message"
                  class="flex-wrap"
                  :color="state.reqResponse.type"
                  :show="!!state.reqResponse.message"
                >
                  {{ state.reqResponse.message }}
                  <div v-if="state.reqResponse.errors" class="w-100">
                    <div
                      v-for="(err, index) in state.reqResponse.errors"
                      :key="index"
                    >
                      {{ err[0] }}
                    </div>
                  </div>
                </CAlert>
                <div class="cs-file-input">
                  <div class="upload-btn">
                    <i class="fi flaticon-upload me-1"></i> BROWSE
                  </div>
                  <input
                    id="csvUsers"
                    type="file"
                    class="form-control"
                    aria-label="Browse, choose CSV file to import users"
                    @change="getFile"
                  />
                </div>

                <div class="text-center mt-4 mb-2">
                  <button
                    v-if="state.params.file"
                    type="button"
                    class="btn bg-gradient-blue text-white px-5"
                    :disabled="state.isLoading"
                    @click="importUsers"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </CCol>
          </CRow>
        </CContainer>
      </div>
      <template #footer-wrapper>
        <div></div>
      </template>
    </CModal>
    <!-- Faild users modal -->
    <CModal
      backdrop="static"
      :visible="state.showSuccessModal"
      :close-on-backdrop="false"
    >
      <div
        ref="importSuccessModalSurfaceRef"
        class="users-import-modal-surface"
      >
        <div class="pt-4 px-4 create-popup-header">
          <button
            type="button"
            class="users-modal-close-btn d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal border-0"
            aria-label="Close"
            @click="state.showSuccessModal = false"
          >
            <span class="close-icon" aria-hidden="true"></span>
          </button>
        </div>
        <CContainer class="px-5 py-4">
          <CRow>
            <CCol md="12">
              <h3>
                Your file has been submitted click
                <router-link
                  class="text-decoration-underline fw-semibold"
                  to="/file-uploads"
                >
                  HERE
                </router-link>
                to be redirected to the file processing screen
              </h3>
            </CCol>
          </CRow>
        </CContainer>
      </div>
      <template #footer-wrapper>
        <div></div>
      </template>
    </CModal>
  </div>
</template>

<script>
import { reactive, ref } from "vue"
import { useStore } from "vuex"
import { useModalFocusTrap } from "@/composables/useModalFocusTrap"
import { useEscapeToCloseModal } from "@/composables/useEscapeToCloseModal"
import HelpCenterButton from "@/v3components/HelpCenterButton.vue"
import RequestErrorsHandler from "@/mixins/RequestErrorsHandler"
import Loader from "@/v3components/shared/Loader/Loader.vue"

export default {
  name: "ImportUsers",
  components: {
    HelpCenterButton,
    Loader
  },
  mixins: [RequestErrorsHandler],
  setup() {
    const store = useStore()

    const importModalSurfaceRef = ref(null)
    const importSuccessModalSurfaceRef = ref(null)

    const state = reactive({
      isLoading: false,
      isModalVisible: false,
      params: {
        auth_type: null,
        fileName: "",
        file: null
      },
      showSuccessModal: false,
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      }
    })

    const getFile = (event) => {
      const files = event.target.files
      if (files && files.length) {
        state.params.file = files[0]
        event.target.value = ""
      }
    }
    const showModal = (event) => {
      if (event.target.classList && event.target.classList.value !== "") {
        state.isModalVisible = true
      }
    }

    const importUsers = () => {
      if (state.params.file) {
        state.isLoading = true
        const data = new FormData()
        data.append("csv_file", state.params.file)
        resetResponseMessages()
        store
          .dispatch("usersImport/importUsers", data)
          .then((response) => {
            const data = response.data.data
            if (data && data.file_name) {
              state.params.fileName = data.file_name
              state.isModalVisible = false
              setTimeout(() => {
                state.showSuccessModal = true
              }, 1000)
            }
            state.isLoading = false
          })
          .catch((err) => {
            state.isLoading = false
            const response = err?.response?.data
            setErrorResponse(response?.message, response?.errors)
          })
      }
    }

    const openFileIpnut = (inputId) => {
      document.getElementById(inputId)?.click?.()
    }

    const resetImportData = () => {
      resetResponseMessages()
      state.params = {
        auth_type: null,
        fileName: "",
        file: null
      }
    }

    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }

    const closeImportModal = () => {
      state.isModalVisible = false
      resetImportData()
    }

    useModalFocusTrap(() => state.isModalVisible, importModalSurfaceRef)
    useModalFocusTrap(
      () => state.showSuccessModal,
      importSuccessModalSurfaceRef
    )

    useEscapeToCloseModal(() => state.isModalVisible, closeImportModal)
    useEscapeToCloseModal(
      () => state.showSuccessModal,
      () => {
        state.showSuccessModal = false
      }
    )

    return {
      importModalSurfaceRef,
      importSuccessModalSurfaceRef,
      state,
      getFile,
      showModal,
      importUsers,
      openFileIpnut,
      resetImportData,
      closeImportModal
    }
  }
}
</script>
